import { Link } from "react-router-dom";
import credentials from "../../assets/credentials"
import AudioPlayer from "../AudioPlayer";
import UrlBox from "./UrlBox"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCoins,
  faCalendarAlt,
  faHeadphones,
  faEdit,
  faShare,
  faUser,
  faGuitar,
  faPlay,
  faMusic,
} from "@fortawesome/free-solid-svg-icons";
import {
  faSpotify,
  faApple,
  faYoutube,
  faAmazon,
  faSoundcloud,
} from "@fortawesome/free-brands-svg-icons";

const MusicViewPage = ({ music, editMusic, hideStats, children, download }) => {
  const handleShare = () => {
    const currentUrl = window.location.href;
    if (navigator.share) {
      navigator
        .share({
          title: music.title,
          text: `Check out ${music.title} by ${music.uploader.stageName}`,
          url: currentUrl,
        })
        .catch((error) => console.error("Error sharing:", error));
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      alert("URL copied to clipboard!");
    }
  };

  // Prioritized order
  const priorityPlatforms = ["spotify", "appleMusic", "amazonMusic"];
  const entries = Object.entries(music.streams || {});

  const sortedEntries = [
    ...entries.filter(([platform]) => priorityPlatforms.includes(platform)),
    ...entries.filter(([platform]) => !priorityPlatforms.includes(platform)),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6">
      <div className="max-w-4xl w-full mx-auto bg-gray-900 shadow-lg rounded-lg p-6 relative">
        {editMusic ? (
          <div className="absolute right-0 top-6 z-50 hover:text-yellow-800 hover:cursor-pointer">
            <Link to={`${editMusic}/${music._id}`}>
              <FontAwesomeIcon icon={faEdit} size="xl" />
            </Link>
          </div>
        ) : (
          <div
            className="absolute right-0 top-6 z-50 hover:text-yellow-800 hover:cursor-pointer"
            onClick={handleShare}>
            <FontAwesomeIcon icon={faShare} size="xl" />
          </div>
        )}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="relative">
            <AudioPlayer url={music.audio} />
            <img
              src={`${credentials.musicImage_URL}/${music.image}`}
              alt="Music Cover"
              className="w-65 max-w-[350px] h-auto min-h-[200px] rounded-lg shadow-md m-auto"
            />
          </div>
          <div className="text-left w-full">
            <h2 className="text-3xl font-bold">{music.title}</h2>
            {music.featuredArtiste[0] !== "" && (
              <p className="text-yellow-400 text-lg">
                <FontAwesomeIcon icon={faUser} className="mr-2 text-yellow-500" />
                ft {music.featuredArtiste.join(", ")}
              </p>
            )}
            <p className="text-gray-300 mt-2">
              <FontAwesomeIcon icon={faGuitar} className="mr-2 text-yellow-500" />
              {music.genre.join(" ")}
            </p>
            <p className="text-gray-400 mt-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-yellow-500" />
              Released: {music.releaseDate.split("T")[0]}
            </p>
            <p className="text-gray-300 mt-4">{music.notes}</p>
          </div>
        </div>
        {download && download}
      </div>

      <div className="max-w-4xl mx-auto mt-6 bg-gray-900 shadow-md p-4 rounded-lg">
        <h3 className="text-xl font-semibold mb-3 text-yellow-400">
          Streaming Platforms
        </h3>
        <div className="space-y-4">
          {sortedEntries.map(([platform, details]) => (
            <div
              key={platform}
              className="flex items-center justify-between flex-row bg-gray-800 p-4 rounded-lg shadow-md py-8 xsm:py-4 relative xsm:static">
              <div className="flex items-center justify-start gap-4 w-full xsm:w-3/4 xsm:justify-start">
                <div>
                  <FontAwesomeIcon
                    icon={
                      platform === "spotify"
                        ? faSpotify
                        : platform === "appleMusic"
                          ? faApple
                          : platform === "youtubeMusic"
                            ? faYoutube
                            : platform === "amazonMusic"
                              ? faAmazon
                              : platform === "soundCloud"
                                ? faSoundcloud
                                : faMusic
                    }
                    className={`text-3xl ${
                      platform === "spotify"
                        ? "text-green-500"
                        : platform === "appleMusic"
                          ? "text-gray-300"
                          : "text-red-500"
                    }`}
                  />
                </div>
                <div>
                  <p className="text-lg font-semibold capitalize">
                    {platform.replace(/([A-Z])/g, " $1")}
                  </p>
                  {!hideStats && ["spotify", "appleMusic", "amazonMusic"].includes(platform) && (
                    <>
                      <p className="text-gray-400">
                        <FontAwesomeIcon icon={faHeadphones} className="mr-0" />{" "}
                        Streams: {details.stream.toLocaleString()}
                      </p>
                      <p className="text-yellow-400 flex items-center">
                        <FontAwesomeIcon icon={faCoins} className="mr-3" />{" "}
                        Earnings: ₦ {details.earning.toFixed(2)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <a
                href={details.link}
                target="_blank"
                rel="noopener noreferrer">
                <FontAwesomeIcon icon={faPlay} className="mr-2 text-yellow-500 text-3xl hover:text-yellow-300" />
              </a>
            </div>
          ))}
        </div>

        <div className="mt-8">{children}</div>

        {!hideStats && (
          <div className="py-4 text-xl">
            <p className="text-gray-400 mb-4">
              <FontAwesomeIcon icon={faHeadphones} className="mr-1" /> Total
              Streams:
              <span className="ml-2">{music.totalStreams || 0}</span>
            </p>
            <p className="text-yellow-400 flex items-center">
              <FontAwesomeIcon icon={faCoins} className="mr-1" /> Total
              Earnings:
              <span className="ml-2">₦ {music.totalEarnings || 0}</span>
            </p>
            {music.uploader?.username && (
              <>
                <h6 className="text-sm -mb-1 mt-2 text-yellow-400">Music Fan Page</h6>
                <UrlBox text={`fanpage/${music.uploader.username}/${music.title}`} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicViewPage;
